import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';
import { useLangStore } from '@/stores/langStore';
import { useConversationStore } from '@/stores/conversationStore';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type { Conversation, LearningTag, LearningTagResponse } from '@/types';

/** The three learning choices, the same as the close dialog's (2026-11-01: now also offered on any conversation). */
const LEARNING_OPTIONS: Array<{
  tag: LearningTag;
  labelKey: 'learningOptionDefault' | 'learningOptionSkip' | 'learningOptionForceLearn';
  titleKey: 'learningOptionDefaultTitle' | 'learningOptionSkipTitle' | 'learningOptionForceLearnTitle';
}> = [
  { tag: 'default', labelKey: 'learningOptionDefault', titleKey: 'learningOptionDefaultTitle' },
  { tag: 'force-learn', labelKey: 'learningOptionForceLearn', titleKey: 'learningOptionForceLearnTitle' },
  { tag: 'skip', labelKey: 'learningOptionSkip', titleKey: 'learningOptionSkipTitle' },
];

interface ConversationMenuProps {
  conv: Conversation;
  /** May this viewer move the conversation between folders (holds workspace.handle and it is not someone else's)? */
  canMove: boolean;
  /** "Return to team", when the header would offer it: the owner's own, or a tenant admin's for someone else's. */
  returnMode: 'mine' | 'admin' | null;
  onReturnToTeam: () => void;
  /** "Spam / unwanted" (email conversations, workspace.handle): opens the existing confirm dialog. */
  onSpam: (() => void) | null;
  /**
   * May this viewer set the "teach the bot from this chat" choice (workspace.handle, not someone else's)? Offered on open
   * AND closed conversations (operator, 2026-11-01): "Learn from this chat" on a closed one asks for its learning article now.
   */
  canLearn?: boolean;
}

/**
 * The conversation's "more" menu (the three dots beside the header; operator 2026-10-12): the less frequent acts live here so
 * the header keeps one line - Move to folder, Return to team, Spam / unwanted. Take over, Close, Release and Let bot continue
 * stay buttons. Every item is offered under exactly the conditions the header used before; the server re-checks each act.
 *
 * The panel is rendered with `hidden` while closed (a disclosure, not a portal), so it follows the root `dir` and its items
 * are neither shown nor focusable until opened. Escape, a click outside, or choosing an item closes it.
 *
 * Move to folder posts the same request as the contact panel's folder picker (POST /api/sessions/{id}/category, which pins
 * the category) through the same store action, optimistic and reverted on failure; the options come from the store, never
 * from a fetch.
 */
export const ConversationMenu: React.FC<ConversationMenuProps> = ({ conv, canMove, returnMode, onReturnToTeam, onSpam, canLearn = false }) => {
  const { t } = useT();
  const lang = useLangStore((s) => s.lang);
  const stateDefs = useConversationStore((s) => s.stateDefs);
  const setConversationCategory = useConversationStore((s) => s.setConversationCategory);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [learning, setLearning] = useState<LearningTag | null>(null);
  const [learningNote, setLearningNote] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const folders = canMove
    ? stateDefs
        .filter((d) => d.isActive)
        .slice()
        .sort((a, b) => a.order - b.order)
    : [];
  const current = conv.category ?? folders.find((d) => d.isDefault)?.key ?? null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Another conversation: nothing half-open carries over.
  useEffect(() => {
    setOpen(false);
    setError(null);
    setLearning(null);
    setLearningNote(null);
  }, [conv.sessionId]);

  // The saved learning choice is read when the menu opens (a failed read shows "Normal", the server's own default).
  useEffect(() => {
    if (!open || !canLearn) return;
    let cancelled = false;
    axiosClient
      .get<LearningTagResponse>(API.LEARNING.GET_TAG(conv.sessionId))
      .then((resp) => {
        if (!cancelled) setLearning(resp?.data?.tag ?? 'default');
      })
      .catch(() => {
        if (!cancelled) setLearning('default');
      });
    return () => {
      cancelled = true;
    };
  }, [open, canLearn, conv.sessionId]);

  if (folders.length === 0 && !returnMode && !onSpam && !canLearn) return null;

  const setLearningTag = async (tag: LearningTag) => {
    if (saving || tag === learning) return;
    const previous = learning;
    setSaving(true);
    setLearning(tag);
    setLearningNote(null);
    try {
      await axiosClient.post(API.LEARNING.SET_TAG(conv.sessionId), { tag });
      setLearningNote(tag === 'force-learn' ? t('conv_menu_learning_saved') : null);
    } catch {
      setLearning(previous);
      setLearningNote(t('conv_menu_learning_failed'));
    } finally {
      setSaving(false);
    }
  };

  const move = async (key: string) => {
    setOpen(false);
    if (key === conv.category || saving) return;
    const previous = conv.category ?? null;
    setSaving(true);
    setError(null);
    setConversationCategory(conv.sessionId, key);
    try {
      await axiosClient.post(API.SESSIONS.SET_CATEGORY(conv.sessionId), { category: key, origin: 'console' });
    } catch {
      setConversationCategory(conv.sessionId, previous);
      setError(t('conv_category_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className="conv-menu-wrap" ref={rootRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        className="ac-icon-btn conv-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('conv_menu_label')}
        title={t('conv_menu_label')}
        onClick={() => setOpen((o) => !o)}
        data-testid="conv-menu-btn"
      >
        <Icon name="dots" size={18} />
      </button>
      <div className="conv-menu" role="menu" hidden={!open} data-testid="conv-menu">
        {folders.length > 0 && (
          <>
            <div className="conv-menu-head">
              <Icon name="folder" size={13} />
              {t('conv_menu_move')}
            </div>
            {folders.map((d) => (
              <button
                key={d.key}
                type="button"
                role="menuitemradio"
                aria-checked={d.key === current}
                className={'conv-menu-item folder' + (d.key === current ? ' on' : '')}
                disabled={saving}
                onClick={() => void move(d.key)}
                data-testid={`conv-menu-folder-${d.key}`}
              >
                <span className="conv-menu-dot" style={{ background: d.color }} />
                <span className="conv-menu-text">{d.label[lang]}</span>
                {d.key === current && <Icon name="check" size={13} />}
              </button>
            ))}
          </>
        )}
        {canLearn && (
          <>
            {folders.length > 0 && <div className="conv-menu-sep" role="separator" />}
            <div className="conv-menu-head">
              <Icon name="sparkle" size={13} />
              {t('conv_menu_learning')}
            </div>
            {LEARNING_OPTIONS.map((o) => (
              <button
                key={o.tag}
                type="button"
                role="menuitemradio"
                aria-checked={learning === o.tag}
                className={'conv-menu-item' + (learning === o.tag ? ' on' : '')}
                title={t(o.titleKey)}
                disabled={saving || learning === null}
                onClick={() => void setLearningTag(o.tag)}
                data-testid={`conv-menu-learning-${o.tag}`}
              >
                <span className="conv-menu-text">{t(o.labelKey)}</span>
                {learning === o.tag && <Icon name="check" size={13} />}
              </button>
            ))}
            {learningNote && (
              <div className="conv-menu-note" role="status" data-testid="conv-menu-learning-note">
                {learningNote}
              </div>
            )}
          </>
        )}
        {(folders.length > 0 || canLearn) && (returnMode || onSpam) && <div className="conv-menu-sep" role="separator" />}
        {returnMode && (
          <button
            type="button"
            role="menuitem"
            className="conv-menu-item"
            title={returnMode === 'mine' ? t('btn_return_team_hint') : undefined}
            onClick={() => {
              setOpen(false);
              onReturnToTeam();
            }}
            data-testid={returnMode === 'mine' ? 'return-team-btn' : 'admin-return-btn'}
          >
            <Icon name="undo" size={14} />
            <span className="conv-menu-text">
              {returnMode === 'mine' ? t('btn_return_team') : t('btn_return_team_admin')}
            </span>
          </button>
        )}
        {onSpam && (
          <button
            type="button"
            role="menuitem"
            className="conv-menu-item danger"
            onClick={() => {
              setOpen(false);
              onSpam();
            }}
            data-testid="spam-btn"
          >
            <Icon name="ban" size={14} />
            <span className="conv-menu-text">{t('spam_btn')}</span>
          </button>
        )}
      </div>
      {error && (
        <span role="alert" className="conv-menu-error" data-testid="conv-menu-error">
          {error}
        </span>
      )}
    </div>
  );
};
